import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, List, ListItem, Typography, ListItemText } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles, listTextStyles, cardRootStyles, collapseCardStyles } from './commonStyles';
import i18n from 'i18n-js';
import { Card, CollapseCard } from 'components/UI';
import ItemStatus from 'components/ItemStatus';
import Loading from 'components/Loading';
import {
  CreateNFCCardModal,
  AssignNFCCardModal,
  AssignToUserNFCCardModal,
} from 'components/Modals';
import { AccessDeviceActions } from 'state/modules/accessDevice';
import { FETCH_STATE } from 'constants/common';
import { useConfirm } from 'hooks';

const NFCDetailsWidget = ({ id, user, view = 'nfc' }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const listClasses = listTextStyles();
  const cardRootClasses = cardRootStyles();
  const collapseCardClasses = collapseCardStyles();
  const [openCreateNFCModal, setOpenCreateNFCModal] = useState(false);
  const [openAssignNFCModal, setOpenAssignNFCModal] = useState(false);
  const [openAssignToUserNFCModal, setOpenAssignToUserNFCModal] = useState(false);
  const {
    accessDevice,
    fetchingAccessDevice,
    updatingAccessDevice,
    assigningAccessDevice,
    unassigningAccessDevice,
  } = useSelector((state) => state.accessDevice);

  const loading = fetchingAccessDevice === FETCH_STATE.LOADING;
  const reload =
    updatingAccessDevice === FETCH_STATE.DONE ||
    assigningAccessDevice === FETCH_STATE.DONE ||
    unassigningAccessDevice === FETCH_STATE.DONE;

  useEffect(() => {
    dispatch(AccessDeviceActions.resetAccessDeviceState());
  }, []);

  useEffect(() => {
    if (id && accessDevice?.id !== id) {
      dispatch(AccessDeviceActions.fetchAccessDeviceById(id));
    } else if (user?.id && accessDevice?.user?.id !== user?.id) {
      dispatch(AccessDeviceActions.fetchAccessDeviceByUserId(user?.id));
    }
  }, [id, user, dispatch]);

  // Run on outdated data
  useEffect(() => {
    if (reload && id) {
      dispatch(AccessDeviceActions.fetchAccessDeviceById(id));
    }
  }, [id, reload, dispatch]);

  useEffect(() => {
    if (reload && user) {
      dispatch(AccessDeviceActions.fetchAccessDeviceByUserId(user?.id));
    }
  }, [user, reload, dispatch]);

  const hasAccessDevice = accessDevice?.id;
  const isAssigned = accessDevice?.user?.id;
  const isNFCView = view === 'nfc';
  const isCustomerView = view === 'customer';

  const handleUnassign = () => {
    dispatch(
      AccessDeviceActions.unassignAccessDevice({
        access_device_id: accessDevice?.id,
        user_id: accessDevice?.user?.id,
      })
    );
  };

  const handleArchiveAccessDevice = () => {
    if (accessDevice?.id) {
      dispatch(AccessDeviceActions.deleteAccessDevice(accessDevice?.id));
    }
  };

  const [toggleUnassign, UnassignConfirm] = useConfirm({
    message: i18n.t('nfc.unassign_confirm'),
    title: i18n.t('nfc.unassign_nfc_card'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: handleUnassign,
    additionalProps: { modalProps: { maxWidth: 700 } },
  });

  const [toggleArchive, ArchiveConfirm] = useConfirm({
    message: i18n.t('nfc.archive_description_nfc_card'),
    title: i18n.t('nfc.archive_nfc_card'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: handleArchiveAccessDevice,
    additionalProps: { modalProps: { maxWidth: 700 } },
  });

  const items = [
    ...(hasAccessDevice && isNFCView
      ? [
          ...(isAssigned
            ? [
                {
                  title: i18n.t('nfc.widget_unassign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => toggleUnassign(),
                },
              ]
            : [
                {
                  title: i18n.t('nfc.widget_assign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => setOpenAssignNFCModal(true),
                },
              ]),
          {
            title: i18n.t('nfc.edit_info'),
            can: { name: 'access_device', action: 'update' },
            onClick: () => {
              setOpenCreateNFCModal(true);
            },
          },
          {
            title: i18n.t('nfc.delete_info'),
            can: { name: 'access_device', action: 'update' },
            onClick: () => toggleArchive(),
          },
        ]
      : []),

    ...(isCustomerView
      ? [
          ...(isAssigned
            ? [
                {
                  title: i18n.t('nfc.widget_unassign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => toggleUnassign(),
                },
              ]
            : [
                {
                  title: i18n.t('nfc.widget_assign_nfc'),
                  can: { name: 'access_device', action: 'update' },
                  onClick: () => {
                    setOpenAssignToUserNFCModal(true);
                  },
                },
              ]),
        ]
      : []),
  ];

  const close =
    updatingAccessDevice === FETCH_STATE.DONE ||
    assigningAccessDevice === FETCH_STATE.DONE ||
    unassigningAccessDevice === FETCH_STATE.DONE;

  useEffect(() => {
    setOpenCreateNFCModal(false);
    setOpenAssignNFCModal(false);
    setOpenAssignToUserNFCModal(false);
  }, [close]);

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      data-cy="nfc-widget-card"
      title={i18n.t('nfc.nfc')}
      contentRootClasses={cardRootClasses}
    >
      <UnassignConfirm />
      <ArchiveConfirm />
      <AssignToUserNFCCardModal
        user={user}
        open={openAssignToUserNFCModal}
        handleClick={() => {
          setOpenAssignToUserNFCModal(false);
        }}
      />
      <AssignNFCCardModal
        id={accessDevice?.id}
        open={openAssignNFCModal}
        handleClick={() => {
          setOpenAssignNFCModal(false);
        }}
      />
      {isNFCView ? (
        <CreateNFCCardModal
          id={accessDevice?.id}
          open={openCreateNFCModal}
          handleClick={() => {
            setOpenCreateNFCModal(false);
          }}
        />
      ) : null}
      {loading ? (
        <Box
          className={classes.loadingContainer}
          justifyContent="center"
          alignItems="center"
          display="flex"
        >
          <Loading />
        </Box>
      ) : (
        <Box className={classes.cardContentPadding}>
          <CollapseCard
            title={i18n.t('nfc.widget_title')}
            dataCy={'collapse-nfc-info'}
            customClasses={collapseCardClasses}
            opened={!isCustomerView}
          >
            {accessDevice?.id && (
              <List className={classes.list}>
                <ListItem>
                  <ListItemText
                    classes={listClasses}
                    primary={`${i18n.t('nfc.card_name')}:`}
                    secondary={accessDevice?.name}
                  />
                </ListItem>
                {accessDevice?.external_type && (
                  <ListItem>
                    <ListItemText
                      classes={listClasses}
                      primary={`${i18n.t('nfc.type')}:`}
                      secondary={accessDevice?.external_type}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText
                    classes={listClasses}
                    primary={`${i18n.t('nfc.serial')}:`}
                    secondary={accessDevice?.device_id}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    classes={listClasses}
                    primary={`${i18n.t('common.status')}:`}
                    secondary={
                      <ItemStatus
                        variant="labelValue"
                        i18nKey="nfc.status"
                        status={accessDevice?.status}
                      />
                    }
                  />
                </ListItem>
                {accessDevice?.brokers?.length > 0 && (
                  <ListItem>
                    <ListItemText
                      classes={listClasses}
                      primary={`${i18n.t('nfc.broker')}:`}
                      secondary={
                        accessDevice?.brokers[0].name.charAt(0).toUpperCase() +
                        accessDevice?.brokers[0].name.slice(1).toLowerCase()
                      }
                    />
                  </ListItem>
                )}
              </List>
            )}
          </CollapseCard>
          {!accessDevice?.id && (
            <Box style={{ margin: '24px 24px 8px 16px' }}>
              <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                <Box fontSize={14} component="span" lineHeight={1}>
                  {i18n.t('nfc.no_nfc')}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
};

NFCDetailsWidget.propTypes = {
  id: PropTypes.number,
  user: PropTypes.object,
  view: PropTypes.string,
};

export default NFCDetailsWidget;
